import React, { useContext, useState, useEffect, useCallback } from "react";
import AuthContext from "../lib/auth-context";
import ThemeContext from "../lib/theme-context";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { styled } from "@mui/material/styles";
import { getBacktestAccount } from "../utils/backtestAccountUtils";

const StyledFormControl = styled(FormControl)(({ theme }) => ({}));

const AccountSelector = ({ value, onChange, size = "small", fullWidth = false, showAggregateOptions = true, variant = "default" }) => {
  const auth = useContext(AuthContext);
  const theme = useContext(ThemeContext);
  const [accounts, setAccounts] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Determine if using compact style (for results tracker)
  const isCompact = variant === "compact";

  const loadAccounts = useCallback(() => {
    if (auth.backtestingMode) {
      // Show only backtesting account in backtest mode
      const backtestAccount = getBacktestAccount();
      setAccounts([backtestAccount]);
    } else {
      // Show only live accounts in live mode
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const capitalAccounts = user.capitalAccounts || [];
      
      // Filter active accounts only (isActive: false means archived)
      const activeAccounts = capitalAccounts.filter(acc => acc.isActive === true);
      setAccounts(activeAccounts);
    }
  }, [auth.backtestingMode]);

  useEffect(() => {
    loadAccounts();
  }, [auth, auth.backtestingMode, loadAccounts]);

  useEffect(() => {
    // Listen for account updates
    const handleAccountUpdate = () => {
      loadAccounts();
    };

    window.addEventListener("accounts-updated", handleAccountUpdate);
    
    return () => {
      window.removeEventListener("accounts-updated", handleAccountUpdate);
    };
  }, [auth.backtestingMode, loadAccounts]);


  const personalAccounts = accounts.filter(acc => acc.type === "Personal");
  const investorAccounts = accounts.filter(acc => acc.type === "Investor");

  // Only show aggregate options if enabled
  const availableOptions = [
    ...(showAggregateOptions ? ["All"] : []),
    ...(showAggregateOptions && personalAccounts.length > 1 ? ["All Personal"] : []),
    ...(showAggregateOptions && investorAccounts.length > 1 ? ["All Investor"] : []),
    ...accounts.map(acc => acc.id)
  ];
  
  const safeValue = availableOptions.includes(value) ? value : "";

  return (
    <StyledFormControl 
      size={size} 
      fullWidth={fullWidth}
      sx={isCompact ? { width: 140 } : {}}
    >
      <Select
        id="account-selector"
        value={safeValue}
        onChange={onChange}
        notched={false}
        displayEmpty
        autoWidth={false}
        open={menuOpen}
        onOpen={() => setMenuOpen(true)}
        onClose={() => {
          setTimeout(() => setMenuOpen(false), 200);
        }}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <span style={{ 
                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                fontSize: isCompact ? "13px" : "inherit",
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}>
                Accounts
              </span>
            );
          }
          const account = accounts.find(acc => acc.id === selected);
          const displayText = account ? account.name : selected;
          return (
            <span style={{ 
              fontSize: isCompact ? "13px" : "14px",
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}>
              {displayText}
            </span>
          );
        }}
        MenuProps={{
          sx: {
            zIndex: 99999,
          },
          PaperProps: {
            sx: isCompact ? {
              maxHeight: 300,
              width: "140px",
              backgroundColor: theme[0] === "dark" ? "rgba(40, 42, 46)" : "#fff",
              color: theme[0] === "dark" ? "#FCFCFF" : "black",
              "& .MuiMenuItem-root": {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "center !important",
                justifyContent: "center !important",
              },
            } : {
              bgcolor: theme[0] === "dark" ? "rgba(40, 42, 46)" : "#FCFCFF",
              maxHeight: "450px",
              "& .MuiMenuItem-root": {
                padding: 1.5,
                justifyContent: "center",
              },
            }
          }
        }}
        sx={isCompact ? {
          backgroundColor: theme[0] === "dark" ? "#3a3d45" : "#e5e7eb",
          color: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
          borderRadius: menuOpen ? "8px 8px 0 0" : "8px",
          fontSize: "13px",
          fontWeight: 600,
          lineHeight: 1.5,
          minHeight: "32px",
          textTransform: "none",
          transition: "border-radius 0.2s ease-in-out",
          ".MuiSvgIcon-root": {
            fill: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
          },
          "& fieldset": {
            border: "none",
          },
          "&:hover": {
            backgroundColor: theme[0] === "dark" ? "#434650" : "#d1d5db",
          },
          "&:hover fieldset": {
            border: "none",
          },
          "&.Mui-focused": {
            backgroundColor: theme[0] === "dark" ? "#3a3d45" : "#e5e7eb",
          },
          "&.Mui-focused fieldset": {
            border: "none",
          },
          "& .MuiSelect-select": {
            paddingTop: "4px",
            paddingBottom: "4px",
            paddingLeft: "14px",
            paddingRight: "14px",
            lineHeight: "24px",
          },
        } : {
          backgroundColor: theme[0] === "dark" ? "#3a3d45" : "#e5e7eb",
          color: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
          borderRadius: menuOpen ? "12px 12px 0 0" : "12px",
          fontSize: "16px",
          fontWeight: 600,
          transition: "border-radius 0.2s ease-in-out",
          ".MuiSvgIcon-root": {
            fill: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
          },
          "& fieldset": {
            border: "none",
          },
          "&:hover": {
            backgroundColor: theme[0] === "dark" ? "#434650" : "#d1d5db",
          },
          "&:hover fieldset": {
            border: "none",
          },
          "&.Mui-focused": {
            backgroundColor: theme[0] === "dark" ? "#3a3d45" : "#e5e7eb",
          },
          "&.Mui-focused fieldset": {
            border: "none",
          },
          "& .MuiSelect-select": {
            paddingTop: "8px",
            paddingBottom: "8px",
            paddingLeft: "12px",
            paddingRight: "12px",
          },
        }}
      >
        {/* All option - show if showAggregateOptions is true */}
        {showAggregateOptions && (
          <MenuItem 
            value="All"
            sx={{
              color: theme[0] === "dark" ? "#FCFCFF" : "rgba(40, 42, 46)",
            }}
          >
            All Accounts
          </MenuItem>
        )}

        {/* All Personal option - only show if showAggregateOptions is true and more than 1 personal account */}
        {showAggregateOptions && personalAccounts.length > 1 && (
          <MenuItem 
            value="All Personal"
            sx={{
              color: theme[0] === "dark" ? "#FCFCFF" : "rgba(40, 42, 46)",
            }}
          >
            All Personal
          </MenuItem>
        )}

        {/* Individual Personal accounts */}
        {personalAccounts.map((account) => (
          <MenuItem 
            key={account.id} 
            value={account.id}
            sx={{
              color: theme[0] === "dark" ? "#FCFCFF" : "rgba(40, 42, 46)",
            }}
          >
            {account.name}
          </MenuItem>
        ))}

        {/* All Investor option - only show if showAggregateOptions is true and more than 1 investor account */}
        {showAggregateOptions && investorAccounts.length > 1 && (
          <MenuItem 
            value="All Investor"
            sx={{
              color: theme[0] === "dark" ? "#FCFCFF" : "rgba(40, 42, 46)",
            }}
          >
            All Investor
          </MenuItem>
        )}

        {/* Individual Investor accounts */}
        {investorAccounts.map((account) => (
          <MenuItem 
            key={account.id} 
            value={account.id}
            sx={{
              color: theme[0] === "dark" ? "#FCFCFF" : "rgba(40, 42, 46)",
            }}
          >
            {account.name}
          </MenuItem>
        ))}
      </Select>
    </StyledFormControl>
  );
};

export default AccountSelector;
